'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import {
  Tractor,
  MapPin,
  Shield,
  Radio,
  Sun,
  AlertTriangle,
  TrendingUp,
  Activity,
  Users,
  Zap,
  Eye,
  Bell,
  BarChart3,
  Heart,
  Droplet,
  Calendar,
  CheckCircle2,
  ArrowRight,
  Satellite,
  Battery,
  Gauge,
  Waves,
  Mountain,
  Beef,
  PiggyBank,
  Target,
  Sparkles,
} from 'lucide-react';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';
import {
  FARMING_SERVICE_MODULE_KEYS,
  TENANT_PACKAGE_LABELS,
} from '@/lib/industry-packages';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const farmingPackageModuleLabel: Record<string, string> = {
  farmersTelemetry: 'Farmers Telemetry',
  reports: 'Reports & Analytics',
  billing: 'Billing Console',
};

const farmingOnboardingSequence = [
  'Select Livestock & Farming industry and package.',
  'Choose plan + workload sizing for farm operations.',
  'Review required farming modules and launch tenant.',
] as const;

const farmingPackageModules = [...FARMING_SERVICE_MODULE_KEYS, 'reports', 'billing'];

export default function LivestockFarmingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ── Hero Section ────────────────────────────────────────── */}
      <section className="relative overflow-hidden gradient-hero pt-32 pb-20 px-6 lg:px-8">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0f0e2e]" />
        
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-7xl mx-auto relative z-10"
        >
          <motion.div variants={itemVariants} className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-neon-teal to-neon-cyan mb-6 glow-cyan"
            >
              <Tractor className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-white">Protect Your Herd,</span>
              <br />
              <span className="text-gradient-cool">Protect Your Livelihood</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
              GPS collar tracking and geofencing revolutionize livestock management. 
              Know where every animal is, every moment. Stop theft before it happens. 
              Monitor health. Optimize grazing. Secure your future.
            </p>

            <div className="mx-auto mb-8 max-w-4xl">
              <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
                <Image
                  src={PAGE_HERO_IMAGES.livestockFarming.src}
                  alt={PAGE_HERO_IMAGES.livestockFarming.alt}
                  width={1600}
                  height={900}
                  className="h-auto w-full rounded-2xl object-cover"
                  priority
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                href="/signup?industry=farming&package=farming"
                className="px-8 py-4 rounded-xl btn-neon text-white font-semibold text-lg flex items-center gap-2 group"
              >
                Start Protecting Your Herd
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/gadgets"
                className="px-8 py-4 rounded-xl btn-neon-outline text-neon-teal font-semibold text-lg flex items-center gap-2"
              >
                View GPS Collars
                <Satellite className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>

          {/* Hero Visual */}
          <motion.div
            variants={itemVariants}
            className="mt-16 relative"
          >
            <div className="glass-card-heavy rounded-3xl p-8 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-neon-teal/10 via-transparent to-neon-cyan/10" />
              <div className="relative z-10 grid md:grid-cols-3 gap-6">
                {[
                  { icon: MapPin, label: 'Real-Time Location', color: 'text-neon-teal' },
                  { icon: Shield, label: 'Theft Prevention', color: 'text-neon-cyan' },
                  { icon: Activity, label: 'Health Monitoring', color: 'text-neon-purple' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + idx * 0.1 }}
                    className="text-center"
                  >
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-4 ${item.color}`}>
                      <item.icon className="w-8 h-8" />
                    </div>
                    <p className="text-white font-medium">{item.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ── The Challenge Section ──────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <AlertTriangle className="w-5 h-5 text-neon-magenta" />
              <span className="text-sm font-semibold text-neon-magenta">The Crisis</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Livestock Theft:</span>
              <br />
              <span className="text-gradient-warm">A R1.4 Billion Problem</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Every year, South African farmers lose billions to livestock theft. 
              Across Africa, the crisis deepens. Traditional methods fail. 
              Your livelihood hangs in the balance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: PiggyBank,
                stat: 'R1.4 Billion',
                label: 'Lost annually in South Africa',
                desc: 'Cattle theft alone accounts for massive financial losses, devastating farming communities.',
                color: 'text-neon-magenta',
                bg: 'from-neon-magenta/10 to-transparent',
              },
              {
                icon: AlertTriangle,
                stat: '70%',
                label: 'Of thefts go unreported',
                desc: 'Farmers lose hope. Without evidence, recovery is impossible. The cycle continues.',
                color: 'text-neon-amber',
                bg: 'from-neon-amber/10 to-transparent',
              },
              {
                icon: Mountain,
                stat: 'Vast Lands',
                label: 'Impossible to monitor manually',
                desc: 'Thousands of hectares. Hundreds of animals. One farmer. Traditional methods fail.',
                color: 'text-neon-teal',
                bg: 'from-neon-teal/10 to-transparent',
              },
              {
                icon: Heart,
                stat: 'Disease Spread',
                label: 'Undetected until too late',
                desc: 'Without movement pattern analysis, diseases spread silently through herds.',
                color: 'text-neon-purple',
                bg: 'from-neon-purple/10 to-transparent',
              },
              {
                icon: Target,
                stat: 'Predator Attacks',
                label: 'No early warning system',
                desc: 'Lions, hyenas, leopards. By the time you discover the attack, animals are already lost.',
                color: 'text-neon-cyan',
                bg: 'from-neon-cyan/10 to-transparent',
              },
              {
                icon: Users,
                stat: 'Manual Counting',
                label: 'Time-consuming and inaccurate',
                desc: 'Hours spent counting. Mistakes happen. Missing animals go unnoticed for days.',
                color: 'text-neon-teal',
                bg: 'from-neon-teal/10 to-transparent',
              },
            ].map((challenge, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-6 relative overflow-hidden group hover:scale-[1.02] transition-transform"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${challenge.bg} opacity-0 group-hover:opacity-100 transition-opacity`} />
                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl glass-card mb-4 ${challenge.color}`}>
                    <challenge.icon className="w-6 h-6" />
                  </div>
                  <div className={`text-3xl font-bold mb-2 ${challenge.color}`}>
                    {challenge.stat}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{challenge.label}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{challenge.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Our Solution Section ──────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section-purple">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <Sparkles className="w-5 h-5 text-neon-teal" />
              <span className="text-sm font-semibold text-neon-teal">The Solution</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Technology That</span>
              <br />
              <span className="text-gradient-cool">Protects What Matters</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Solar-powered GPS collars. Real-time tracking. Instant alerts. 
              Your animals, monitored 24/7. Your peace of mind, restored.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              {
                icon: Satellite,
                title: 'GPS Solar-Powered Collars',
                desc: 'Never worry about battery life. Our collars charge themselves using solar power, providing continuous tracking for years. IP68 rated for harsh African conditions.',
                features: ['Solar charging', '2-year battery life', 'IP68 waterproof', 'Rugged design'],
                color: 'text-neon-teal',
              },
              {
                icon: MapPin,
                title: 'Real-Time Location Tracking',
                desc: 'See every animal on your map, updated in real-time. Know exactly where your herd is grazing, moving, or if they\'ve wandered into danger zones.',
                features: ['Live GPS updates', 'Map visualization', 'Historical routes', 'Movement patterns'],
                color: 'text-neon-cyan',
              },
              {
                icon: Shield,
                title: 'Geofence Alerts',
                desc: 'Set virtual boundaries. The moment an animal leaves a designated area, you receive an instant alert. Stop theft before it happens. Prevent wandering.',
                features: ['Custom boundaries', 'Instant notifications', 'Multi-channel alerts', 'Theft prevention'],
                color: 'text-neon-purple',
              },
              {
                icon: Activity,
                title: 'Movement Pattern Analysis',
                desc: 'AI-powered health monitoring. Unusual movement patterns signal illness, injury, or distress. Detect problems early. Save animals. Save money.',
                features: ['Anomaly detection', 'Health insights', 'Behavioral analysis', 'Predictive alerts'],
                color: 'text-neon-magenta',
              },
            ].map((solution, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-neon-teal/5 via-transparent to-neon-cyan/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-6 ${solution.color}`}>
                    <solution.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{solution.title}</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">{solution.desc}</p>
                  <ul className="space-y-2">
                    {solution.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-2 text-slate-300">
                        <CheckCircle2 className={`w-4 h-4 ${solution.color}`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tenant Farming Modules ───────────────────────────── */}
      <section className="relative py-20 px-6 lg:px-8 gradient-section-purple">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-4">
              <Sparkles className="w-4 h-4 text-neon-teal" />
              <span className="text-xs font-semibold uppercase tracking-wide text-neon-teal">
                Enterprise Tenant Package Rollout
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {TENANT_PACKAGE_LABELS.farming}
            </h2>
            <p className="mt-3 text-slate-300 max-w-3xl mx-auto">
              Farming tenants are provisioned with telemetry-first modules for land boundaries,
              livestock movement, billing governance, and performance reporting.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="glass-card-heavy rounded-2xl p-6"
          >
            <div className="grid gap-3 md:grid-cols-3">
              {farmingOnboardingSequence.map((step, idx) => (
                <div
                  key={step}
                  className="rounded-xl border border-white/10 bg-slate-950/25 px-3 py-2"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-neon-cyan">
                    Sequence {idx + 1}
                  </p>
                  <p className="mt-1 text-xs text-slate-300">{step}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {farmingPackageModules.map((moduleKey) => (
                <span
                  key={moduleKey}
                  className="rounded-full border border-neon-teal/40 bg-neon-teal/10 px-3 py-1 text-[11px] font-medium text-neon-teal"
                >
                  {farmingPackageModuleLabel[moduleKey] ?? moduleKey}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Features Grid ──────────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Everything You Need to</span>
              <br />
              <span className="text-gradient-cool">Manage Your Herd</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Comprehensive features designed for modern livestock management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sun, title: 'Solar GPS Collars', desc: 'Self-charging, maintenance-free tracking devices' },
              { icon: Radio, title: 'LoRa + 4G Connectivity', desc: 'Dual connectivity ensures coverage in remote areas' },
              { icon: MapPin, title: 'Geofencing', desc: 'Virtual boundaries with instant breach alerts' },
              { icon: Users, title: 'Herd Counting', desc: 'Automated counting and inventory management' },
              { icon: BarChart3, title: 'Movement Analytics', desc: 'Track grazing patterns and optimize land use' },
              { icon: Activity, title: 'Health Anomaly Detection', desc: 'AI identifies unusual behavior indicating illness' },
              { icon: Bell, title: 'Anti-Theft Alerts', desc: 'Instant notifications when animals leave safe zones' },
              { icon: Shield, title: 'Farm Boundary Monitoring', desc: 'Protect your property with virtual perimeters' },
              { icon: Droplet, title: 'Water Source Tracking', desc: 'Monitor water consumption and source visits' },
              { icon: Calendar, title: 'Breeding Cycle Tracking', desc: 'Optimize breeding schedules and health management' },
              { icon: Eye, title: 'Real-Time Visibility', desc: 'See every animal on your dashboard, 24/7' },
              { icon: Zap, title: 'Instant Notifications', desc: 'SMS, email, and app alerts for critical events' },
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card rounded-xl p-6 hover:scale-[1.02] transition-transform group"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg glass-card mb-4 text-neon-teal group-hover:text-neon-cyan transition-colors">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                <p className="text-slate-300 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases Section ─────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section-purple">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Built for Every</span>
              <br />
              <span className="text-gradient-cool">Type of Operation</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              From small family farms to large commercial operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Beef,
                title: 'Cattle Ranching',
                desc: 'Protect your cattle from theft, monitor grazing patterns, and optimize herd health across vast ranches.',
                color: 'text-neon-teal',
              },
              {
                icon: Mountain,
                title: 'Game Farming',
                desc: 'Track wildlife on reserves, monitor movement patterns, and protect endangered species from poaching.',
                color: 'text-neon-cyan',
              },
              {
                icon: Users,
                title: 'Sheep & Goat Farming',
                desc: 'Manage smaller livestock with precision tracking, prevent losses, and optimize breeding cycles.',
                color: 'text-neon-purple',
              },
              {
                icon: Tractor,
                title: 'Dairy Farms',
                desc: 'Monitor dairy cattle health, track milking schedules, and ensure optimal conditions for production.',
                color: 'text-neon-magenta',
              },
              {
                icon: BarChart3,
                title: 'Feedlots',
                desc: 'Track animals through feeding cycles, monitor health metrics, and optimize feed efficiency.',
                color: 'text-neon-amber',
              },
            ].map((useCase, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 text-center group hover:scale-[1.02] transition-transform"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-6 mx-auto ${useCase.color}`}>
                  <useCase.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{useCase.title}</h3>
                <p className="text-slate-300 leading-relaxed">{useCase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hardware Spotlight ────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <Radio className="w-5 h-5 text-neon-teal" />
              <span className="text-sm font-semibold text-neon-teal">Hardware</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">GT-Collar V2:</span>
              <br />
              <span className="text-gradient-cool">Built for Africa</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Rugged, reliable, and designed to withstand the harshest conditions
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card-heavy rounded-3xl p-8 md:p-12 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-neon-teal/10 via-transparent to-neon-cyan/10" />
            <div className="relative z-10">
              <div className="grid md:grid-cols-2 gap-12 items-center mb-8">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-6">Specifications</h3>
                  <div className="space-y-4">
                    {[
                      { icon: Sun, label: 'Solar-Powered', value: 'Self-charging, maintenance-free' },
                      { icon: Shield, label: 'IP68 Rated', value: 'Waterproof, dustproof, rugged' },
                      { icon: Radio, label: 'LoRa + 4G', value: 'Dual connectivity for remote areas' },
                      { icon: Battery, label: '2-Year Battery', value: 'Extended life with solar backup' },
                      { icon: Gauge, label: 'Lightweight', value: 'Minimal impact on animal comfort' },
                      { icon: Waves, label: 'Long Range', value: 'Works in vast open spaces' },
                    ].map((spec, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg glass-card text-neon-teal flex-shrink-0">
                          <spec.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-white font-semibold mb-1">{spec.label}</div>
                          <div className="text-slate-300 text-sm">{spec.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-48 h-48 rounded-3xl glass-card mx-auto mb-6 glow-cyan">
                    <Satellite className="w-24 h-24 text-neon-teal" />
                  </div>
                  <p className="text-slate-300 leading-relaxed">
                    The GT-Collar V2 is engineered specifically for African farming conditions. 
                    Extreme temperatures, dust, rain, and vast distances—we&apos;ve built it to handle everything.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/gadgets"
                  className="px-8 py-4 rounded-xl btn-neon text-white font-semibold flex items-center justify-center gap-2"
                >
                  View Full Specifications
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  href="/contact"
                  className="px-8 py-4 rounded-xl btn-neon-outline text-neon-teal font-semibold flex items-center justify-center gap-2"
                >
                  Request Quote
                  <Target className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── ROI Section ───────────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-section-purple">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <TrendingUp className="w-5 h-5 text-neon-teal" />
              <span className="text-sm font-semibold text-neon-teal">Return on Investment</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Protect Your</span>
              <br />
              <span className="text-gradient-cool">Bottom Line</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              The cost of tracking is far less than the cost of loss
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                stat: '90%',
                label: 'Reduction in Theft Losses',
                desc: 'Farmers using GPS tracking report dramatic decreases in livestock theft. Early alerts mean faster response.',
                icon: Shield,
                color: 'text-neon-teal',
              },
              {
                stat: '24/7',
                label: 'Real-Time Visibility',
                desc: 'Know where every animal is, every moment. No more guessing. No more manual counting. Complete peace of mind.',
                icon: Eye,
                color: 'text-neon-cyan',
              },
              {
                stat: 'Predictive',
                label: 'Health Insights',
                desc: 'Detect illness before symptoms appear. Save animals. Reduce veterinary costs. Optimize herd health.',
                icon: Activity,
                color: 'text-neon-purple',
              },
            ].map((roi, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card-heavy rounded-2xl p-8 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl glass-card mb-6 mx-auto ${roi.color}`}>
                  <roi.icon className="w-8 h-8" />
                </div>
                <div className={`text-5xl font-bold mb-4 ${roi.color}`}>
                  {roi.stat}
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{roi.label}</h3>
                <p className="text-slate-300 leading-relaxed">{roi.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 glass-card rounded-2xl p-8 text-center"
          >
            <p className="text-lg text-slate-300 mb-6">
              <span className="text-white font-semibold">Calculate your savings:</span> If you lose just 5 cattle per year at R15,000 each, 
              that&apos;s R75,000 in losses. GPS tracking pays for itself in months, not years.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl btn-neon text-white font-semibold"
            >
              Calculate Your ROI
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── CTA Section ────────────────────────────────────────── */}
      <section className="relative py-24 px-6 lg:px-8 gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-neon-teal to-neon-cyan mb-8 glow-cyan mx-auto"
            >
              <Shield className="w-10 h-10 text-white" />
            </motion.div>
            
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white">Ready to Protect</span>
              <br />
              <span className="text-gradient-cool">Your Livelihood?</span>
            </h2>
            
            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              Join hundreds of farmers across Africa who trust GlonexTrack to protect their herds. 
              Stop losses. Start tracking. Secure your future today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                href="/signup?industry=farming&package=farming"
                className="px-10 py-5 rounded-xl btn-neon text-white font-semibold text-lg flex items-center gap-2 group"
              >
                Get Started Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/pricing"
                className="px-10 py-5 rounded-xl btn-neon-outline text-neon-teal font-semibold text-lg flex items-center gap-2"
              >
                View Pricing
                <TrendingUp className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-300">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-neon-teal" />
                <span>No setup fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-neon-teal" />
                <span>30-day money-back guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-neon-teal" />
                <span>24/7 support</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
